import { memo, useCallback, useMemo } from 'react';

import { Info } from '@air/next-icons';
import { Slider, type SliderProps } from '@air/primitive-slider';
import { Tooltip } from '@air/primitive-tooltip';

const MAX_VALUE = 90;

const MIN_VALUE = 30;

export type ColorThresholdControlProps = Pick<SliderProps, 'defaultValue'> & {
  onThresholdChange: (value: number) => void;
};

export const ColorThresholdControl = memo(
  ({ defaultValue: _defaultValue, onThresholdChange }: ColorThresholdControlProps) => {
    const defaultValue = useMemo(() => {
      const value = (_defaultValue?.[0] ?? 0) * 100;
      const invertedValue = MAX_VALUE + MIN_VALUE - value;

      return [invertedValue];
    }, [_defaultValue]);

    const onValueCommit = useCallback(
      (value: number[]) => {
        const invertedValue = MAX_VALUE + MIN_VALUE - value[0];
        const threshold = invertedValue / 100;

        onThresholdChange(threshold);
      },
      [onThresholdChange],
    );

    return (
      <div className="flex w-full flex-col items-start gap-3" data-testid="COLOR_RANGE_CONTROL">
        <div className="flex items-center gap-1">
          <p className="font-medium text-12 text-grey-12">Color range</p>
          <Tooltip
            label="Adjust how closely colors need to match. Lower settings show exact matches. Higher settings include a wider range of similar hues, tones, and shades."
            className="w-[240px]"
          >
            <Info className="size-4 text-grey-8" />
          </Tooltip>
        </div>

        <div className="flex w-full flex-col items-start gap-3 rounded-8 border border-grey-5 px-4 py-3 pb-4">
          <div className="flex w-full items-center justify-between">
            <p className="text-10 text-grey-12">Narrow</p>
            <p className="text-10 text-grey-12">Wide</p>
          </div>

          <Slider
            rangeColor="grey"
            defaultValue={defaultValue}
            onValueCommit={onValueCommit}
            step={1}
            max={MAX_VALUE}
            min={MIN_VALUE}
            data-testid="COLOR_RANGE_SLIDER"
          />
        </div>
      </div>
    );
  },
);

ColorThresholdControl.displayName = 'ColorThresholdControl';
